import { Event, request } from '@mirai/data-sources';
import { useCallback, useEffect, useRef } from 'react';

import { EVENT, METRICS_EVENTS } from '../../../../helpers';
import { getClientFingerprint } from '../../../helpers';
import { getAllowedOpenUrl } from '../helpers';

const AUDIO_CONSTRAINTS = { autoGainControl: true, echoCancellation: true, noiseSuppression: true };
const CLOSE_TIMEOUT_RESULT = {};
const DATA_CHANNEL_LABEL = 'oai-events';
const ERROR_AUDIO_PLAYBACK_BLOCKED = 'audio_playback_blocked';
const ERROR_BLOCKED_OPEN_URL = 'blocked_url';
const ERROR_ICE_TIMEOUT = 'Timed out while gathering ICE candidates';
const ERROR_MISSING_AUDIO_CONTEXT = 'AudioContext not supported';
const ERROR_MISSING_LOCAL_SDP = 'Missing local SDP offer';
const ERROR_MISSING_REMOTE_SDP = 'Missing remote SDP answer';
const ERROR_SESSION_CLOSE_UNCONFIRMED = 'session_close_unconfirmed';
const FFT_SIZE = 1024;
const ICE_GATHERING_TIMEOUT = 10000;
const SESSION_CLOSE_TIMEOUT = 15000;
const SESSION_ENDPOINT = '/voice/live/session';
const TERMINAL_CONNECTION_STATES = ['closed', 'failed'];
const SSE_EVENT_CLOSED = 'closed';
const SSE_EVENT_OPEN_URL = 'open_url';

const createConnectionState = () => ({ connected: false, ready: false, receiving: false, started: false });

const parseServerEvent = (value) => {
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const stopMediaStream = (mediaStream) => {
  if (!mediaStream) return;
  mediaStream.getTracks().forEach((track) => track.stop());
};

const useLiveSession = () => {
  const attemptRef = useRef(0);
  const audioContextRef = useRef(null);
  const audioElementRef = useRef(null);
  const closingRef = useRef(null);
  const connectionStateRef = useRef(createConnectionState());
  const dataChannelRef = useRef(null);
  const eventSourceRef = useRef(null);
  const fingerprintRef = useRef(null);
  const inputFrequencyDataRef = useRef(null);
  const inputSourceRef = useRef(null);
  const localStreamRef = useRef(null);
  const outputFrequencyDataRef = useRef(null);
  const outputSourceRef = useRef(null);
  const pageHideHandlerRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const sessionIdRef = useRef(null);
  const audioAnalyzersRef = useRef({
    inputAnalyser: null,
    outputAnalyser: null,
    getInputByteFrequencyData: () => null,
    getOutputByteFrequencyData: () => null,
  });

  const cleanupLiveSession = useCallback(() => {
    attemptRef.current += 1;

    if (pageHideHandlerRef.current) {
      window.removeEventListener('pagehide', pageHideHandlerRef.current);
      pageHideHandlerRef.current = null;
    }

    const eventSource = eventSourceRef.current;
    if (eventSource) {
      eventSource.close();
      eventSourceRef.current = null;
    }

    const dataChannel = dataChannelRef.current;
    if (dataChannel) {
      dataChannel.close();
      dataChannelRef.current = null;
    }

    const peerConnection = peerConnectionRef.current;
    if (peerConnection) {
      peerConnection.getSenders().forEach(({ track }) => {
        if (track) track.stop();
      });
      peerConnection.close();
      peerConnectionRef.current = null;
    }

    stopMediaStream(localStreamRef.current);
    localStreamRef.current = null;

    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }

    if (inputSourceRef.current) {
      inputSourceRef.current.disconnect();
      inputSourceRef.current = null;
    }

    if (outputSourceRef.current) {
      outputSourceRef.current.disconnect();
      outputSourceRef.current = null;
    }

    audioAnalyzersRef.current.inputAnalyser = null;
    audioAnalyzersRef.current.outputAnalyser = null;
    audioAnalyzersRef.current.getInputByteFrequencyData = () => null;
    audioAnalyzersRef.current.getOutputByteFrequencyData = () => null;
    inputFrequencyDataRef.current = null;
    outputFrequencyDataRef.current = null;

    const audioContext = audioContextRef.current;
    if (audioContext) {
      Promise.resolve(audioContext.close()).catch(() => {});
      audioContextRef.current = null;
    }

    closingRef.current = null;
    connectionStateRef.current = createConnectionState();
    fingerprintRef.current = null;
    sessionIdRef.current = null;
  }, []);

  const releaseLiveSession = useCallback(() => {
    if (closingRef.current) return;

    cleanupLiveSession();
  }, [cleanupLiveSession]);

  useEffect(() => releaseLiveSession, [releaseLiveSession]);

  const createAudioAnalyzers = useCallback(async (mediaStream, isCurrentAttempt) => {
    const AudioContextConstructor = globalThis.AudioContext || globalThis.webkitAudioContext;
    if (!AudioContextConstructor) throw new Error(ERROR_MISSING_AUDIO_CONTEXT);

    const audioContext = new AudioContextConstructor();
    if (audioContext.state === 'suspended') await audioContext.resume();

    if (!isCurrentAttempt()) {
      Promise.resolve(audioContext.close()).catch(() => {});
      return false;
    }

    audioContextRef.current = audioContext;
    const inputAnalyser = audioContext.createAnalyser();
    const outputAnalyser = audioContext.createAnalyser();
    inputAnalyser.fftSize = FFT_SIZE;
    outputAnalyser.fftSize = FFT_SIZE;
    audioAnalyzersRef.current.inputAnalyser = inputAnalyser;
    audioAnalyzersRef.current.outputAnalyser = outputAnalyser;
    inputFrequencyDataRef.current = new Uint8Array(inputAnalyser.frequencyBinCount);
    outputFrequencyDataRef.current = new Uint8Array(outputAnalyser.frequencyBinCount);
    audioAnalyzersRef.current.getInputByteFrequencyData = () => {
      if (!audioAnalyzersRef.current.inputAnalyser || !inputFrequencyDataRef.current) return null;
      audioAnalyzersRef.current.inputAnalyser.getByteFrequencyData(inputFrequencyDataRef.current);
      return inputFrequencyDataRef.current;
    };
    audioAnalyzersRef.current.getOutputByteFrequencyData = () => {
      if (!audioAnalyzersRef.current.outputAnalyser || !outputFrequencyDataRef.current) return null;
      audioAnalyzersRef.current.outputAnalyser.getByteFrequencyData(outputFrequencyDataRef.current);
      return outputFrequencyDataRef.current;
    };

    const inputSource = audioContext.createMediaStreamSource(mediaStream);
    inputSourceRef.current = inputSource;
    inputSource.connect(inputAnalyser);

    return true;
  }, []);

  const waitForIceGathering = useCallback(
    (peerConnection) =>
      new Promise((resolve, reject) => {
        if (peerConnection.iceGatheringState === 'complete') {
          resolve();
          return;
        }

        let timeoutId = null;
        const handleStateChange = () => {
          if (peerConnection.iceGatheringState !== 'complete') return;
          clearTimeout(timeoutId);
          peerConnection.removeEventListener('icegatheringstatechange', handleStateChange);
          resolve();
        };

        timeoutId = setTimeout(() => {
          peerConnection.removeEventListener('icegatheringstatechange', handleStateChange);
          reject(new Error(ERROR_ICE_TIMEOUT));
        }, ICE_GATHERING_TIMEOUT);

        peerConnection.addEventListener('icegatheringstatechange', handleStateChange);
      }),
    [],
  );

  const registerPageHide = useCallback((fingerprint) => {
    const handlePageHide = () => {
      const sessionId = sessionIdRef.current;
      if (!sessionId || !fingerprint || typeof navigator.sendBeacon !== 'function') return;
      const query = new URLSearchParams({ fingerprint }).toString();
      navigator.sendBeacon(`${process.env.SERVICE_SYNAPSE}${SESSION_ENDPOINT}/${sessionId}/close?${query}`);
    };

    pageHideHandlerRef.current = handlePageHide;
    window.addEventListener('pagehide', handlePageHide);
  }, []);

  const openEventStream = useCallback(({ fingerprint, onClosed, onOpenUrl, sessionId }) => {
    const EventSourceConstructor = globalThis.EventSource;
    if (!EventSourceConstructor || !sessionId || !fingerprint) return;

    const query = new URLSearchParams({ fingerprint }).toString();
    const eventSource = new EventSourceConstructor(
      `${process.env.SERVICE_SYNAPSE}${SESSION_ENDPOINT}/${sessionId}/events?${query}`,
    );
    eventSourceRef.current = eventSource;
    eventSource.addEventListener(SSE_EVENT_OPEN_URL, onOpenUrl);
    eventSource.addEventListener(SSE_EVENT_CLOSED, onClosed);
  }, []);

  const playRemoteAudio = useCallback(() => {
    const audioElement = audioElementRef.current;
    if (!audioElement || typeof audioElement.play !== 'function') return;

    const reportBlockedPlayback = () => {
      Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: ERROR_AUDIO_PLAYBACK_BLOCKED });
    };

    try {
      const played = audioElement.play();
      if (played && typeof played.catch === 'function') played.catch(reportBlockedPlayback);
    } catch {
      reportBlockedPlayback();
    }
  }, []);

  const endSession = useCallback(async () => {
    if (closingRef.current) {
      await closingRef.current;
      return;
    }

    const attempt = attemptRef.current;
    const isCurrentAttempt = () => attemptRef.current === attempt;
    const fingerprint = fingerprintRef.current;
    const sessionId = sessionIdRef.current;
    if (!fingerprint || !sessionId) {
      cleanupLiveSession();
      return;
    }

    let timeoutId = null;
    const closing = Promise.race([
      request({
        endpoint: `${SESSION_ENDPOINT}/${sessionId}/close`,
        hostname: process.env.SERVICE_SYNAPSE,
        method: 'POST',
        fingerprint,
      }),
      new Promise((resolve) => {
        timeoutId = setTimeout(() => resolve(CLOSE_TIMEOUT_RESULT), SESSION_CLOSE_TIMEOUT);
      }),
    ]);
    closingRef.current = closing;

    try {
      const result = await closing;
      if (result === CLOSE_TIMEOUT_RESULT) {
        Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: ERROR_SESSION_CLOSE_UNCONFIRMED });
      }
    } catch (error) {
      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.AURA_ERROR,
        error: ERROR_SESSION_CLOSE_UNCONFIRMED,
        reason: error?.message,
      });
    } finally {
      clearTimeout(timeoutId);
      if (isCurrentAttempt()) cleanupLiveSession();
    }
  }, [cleanupLiveSession]);

  const startSession = useCallback(
    async ({ chainId, currency, hotelId, isChain, locale, setIsConnecting, setReady }) => {
      cleanupLiveSession();
      attemptRef.current += 1;

      const attempt = attemptRef.current;
      const isCurrentAttempt = () => attemptRef.current === attempt;

      let createdSessionId = null;
      let dataChannel = null;
      let mediaStream = null;
      let peerConnection = null;

      const releaseAttempt = () => {
        if (dataChannel) dataChannel.close();
        if (peerConnection) {
          peerConnection.getSenders().forEach(({ track }) => {
            if (track) track.stop();
          });
          peerConnection.close();
        }
        stopMediaStream(mediaStream);
        if (!createdSessionId) return;

        const abandonedSessionId = createdSessionId;
        createdSessionId = null;
        request({
          endpoint: `${SESSION_ENDPOINT}/${abandonedSessionId}/close`,
          hostname: process.env.SERVICE_SYNAPSE,
          method: 'POST',
          fingerprint,
        }).catch(() => {});
      };

      const abortWhenStale = () => {
        if (isCurrentAttempt()) return false;
        releaseAttempt();
        return true;
      };

      const fingerprint = getClientFingerprint();
      fingerprintRef.current = fingerprint;
      registerPageHide(fingerprint);

      const finalizeSession = () => {
        if (!isCurrentAttempt()) return;
        cleanupLiveSession();
        setReady(false);
        setIsConnecting(false);
      };

      const markReady = () => {
        if (!isCurrentAttempt()) return;
        const { connected, ready, receiving, started } = connectionStateRef.current;
        if (ready || !connected || !receiving || !started) return;
        connectionStateRef.current.ready = true;
        Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_START });
        setReady(true);
        setIsConnecting(false);
      };

      const handleSessionClosed = (payload) => {
        const { reason } = payload;
        Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_USAGE, reason, seconds: payload.usage?.seconds });
        if (closingRef.current) return;
        finalizeSession();
      };

      const handleServerEvent = ({ data }) => {
        if (!isCurrentAttempt()) return;
        const payload = parseServerEvent(data);
        if (!payload) return;
        const { error, session, type } = payload;

        if (type === 'session.started') {
          sessionIdRef.current = session?.id ?? sessionIdRef.current;
          connectionStateRef.current.started = true;
          markReady();
        }
        if (type === 'session.closed') handleSessionClosed(payload);
        if (type === 'error') Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: error?.message });
      };

      const handleStreamClosed = () => {
        if (!isCurrentAttempt() || closingRef.current) return;
        finalizeSession();
      };

      const handleStreamOpenUrl = ({ data }) => {
        if (!isCurrentAttempt()) return;
        const { url } = parseServerEvent(data) || {};
        const allowedUrl = getAllowedOpenUrl(url);

        if (!allowedUrl) {
          Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: ERROR_BLOCKED_OPEN_URL });
          return;
        }

        Promise.resolve(endSession())
          .catch(() => {})
          .then(() => window.location.assign(allowedUrl));
      };

      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
        if (abortWhenStale()) return;
        localStreamRef.current = mediaStream;

        const analyzersReady = await createAudioAnalyzers(mediaStream, isCurrentAttempt);
        if (!analyzersReady || abortWhenStale()) return;

        peerConnection = new RTCPeerConnection();
        peerConnectionRef.current = peerConnection;

        audioElementRef.current = document.createElement('audio');
        audioElementRef.current.autoplay = true;

        peerConnection.addEventListener('connectionstatechange', () => {
          if (!isCurrentAttempt()) return;
          const { connectionState } = peerConnection;
          if (connectionState === 'connected') {
            connectionStateRef.current.connected = true;
            markReady();
            return;
          }
          if (TERMINAL_CONNECTION_STATES.includes(connectionState)) finalizeSession();
        });

        peerConnection.ontrack = ({ streams }) => {
          if (!isCurrentAttempt()) return;
          const [remoteStream] = streams || [];
          if (!remoteStream) return;

          if (audioElementRef.current) audioElementRef.current.srcObject = remoteStream;
          playRemoteAudio();

          const audioContext = audioContextRef.current;
          const { outputAnalyser } = audioAnalyzersRef.current;
          if (audioContext && outputAnalyser) {
            if (outputSourceRef.current) outputSourceRef.current.disconnect();
            outputSourceRef.current = audioContext.createMediaStreamSource(remoteStream);
            outputSourceRef.current.connect(outputAnalyser);
          }

          connectionStateRef.current.receiving = true;
          markReady();
        };

        dataChannel = peerConnection.createDataChannel(DATA_CHANNEL_LABEL);
        dataChannelRef.current = dataChannel;
        dataChannel.addEventListener('message', handleServerEvent);
        dataChannel.addEventListener('close', () => {
          if (!isCurrentAttempt() || closingRef.current) return;
          finalizeSession();
        });
        dataChannel.addEventListener('error', () => {
          if (!isCurrentAttempt()) return;
          finalizeSession();
        });

        const [track] = mediaStream.getAudioTracks();
        if (track) peerConnection.addTrack(track, mediaStream);

        const offer = await peerConnection.createOffer();
        if (abortWhenStale()) return;

        await peerConnection.setLocalDescription(offer);
        if (abortWhenStale()) return;

        await waitForIceGathering(peerConnection);
        if (abortWhenStale()) return;

        const { sdp } = peerConnection.localDescription || {};
        if (!sdp) throw new Error(ERROR_MISSING_LOCAL_SDP);

        const { session, transport } = await request({
          endpoint: SESSION_ENDPOINT,
          hostname: process.env.SERVICE_SYNAPSE,
          method: 'POST',
          chainId: isChain ? chainId : undefined,
          currency,
          fingerprint,
          hotelId: isChain ? undefined : hotelId,
          locale,
          sdp,
        });
        createdSessionId = session?.id ?? null;
        if (abortWhenStale()) return;

        if (!transport?.sdp) throw new Error(ERROR_MISSING_REMOTE_SDP);
        sessionIdRef.current = createdSessionId;

        await peerConnection.setRemoteDescription({ type: 'answer', sdp: transport.sdp });
        if (abortWhenStale()) return;

        openEventStream({
          fingerprint,
          onClosed: handleStreamClosed,
          onOpenUrl: handleStreamOpenUrl,
          sessionId: sessionIdRef.current,
        });
      } catch (error) {
        releaseAttempt();
        Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: error?.message });
        if (isCurrentAttempt()) {
          cleanupLiveSession();
          setReady(false);
          setIsConnecting(false);
        }
        throw error;
      }
    },
    [
      cleanupLiveSession,
      createAudioAnalyzers,
      endSession,
      openEventStream,
      playRemoteAudio,
      registerPageHide,
      waitForIceGathering,
    ],
  );

  return { startSession, endSession, audioAnalyzers: audioAnalyzersRef.current };
};

export { useLiveSession };
